import React, { useState } from "react";
import { useToast } from "../../hooks/use-toast";
import ConversationIdFinder from "../shared/ConversationIdFinder";
import LambdaRunStatus from "../shared/LambdaRunStatus";
import { mergeAudioChunksForConversation } from "./patientApi";
import { environment } from "../../config/environment";

const supportButtonClass =
  "inline-flex items-center px-3 py-1.5 text-sm bg-ktx-purple text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed";

const formatConversationLabel = (conversationId) => {
  if (!conversationId) return "—";
  const segments = conversationId.split("/");
  return segments[segments.length - 1] || conversationId;
};

const GenerateTranscriptAction = ({ patient }) => {
  const { toast } = useToast();
  const patientId = patient?.patient_id;
  const [mergingId, setMergingId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [lastConversationId, setLastConversationId] = useState(null);

  const handleMerge = async (conversationId) => {
    if (!conversationId || mergingId) return;

    setMergingId(conversationId);
    setRuns([]);

    const result = await mergeAudioChunksForConversation(conversationId);

    setMergingId(null);
    setRuns(result.runs || []);
    setLastConversationId(conversationId);

    if (result.success) {
      toast({
        title: "Transcript generation started",
        description: formatConversationLabel(conversationId),
      });
      return;
    }

    toast({
      variant: "destructive",
      title: "Could not generate transcript",
      description: result.error || "Failed to start merge.",
    });
  };

  return (
    <div className="space-y-2">
      <ConversationIdFinder
        patientId={patientId}
        onSelectConversation={handleMerge}
        actionLabel="Generate transcript"
        actionLoadingId={mergingId}
        description={`Loads unique conversation IDs from ${environment.realtimeAudioChunksTable} for this patient, then runs merge for the recording you choose.`}
        refreshLabel="Refresh conversations"
        refreshButtonClassName={`${supportButtonClass} text-sm`}
        actionButtonClassName={`${supportButtonClass} text-xs py-1.5 px-2 h-7`}
      />

      {runs.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600">
            {formatConversationLabel(lastConversationId)}
          </span>
          <LambdaRunStatus runs={runs} title="Transcript generation logs" />
        </div>
      )}
    </div>
  );
};

export default GenerateTranscriptAction;
